import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {ApiService} from '../../shared/services/api.service';
import {ConfigService} from '../../shared/services/config.service';
import {TranslatePipe} from '../../shared/pipes/translate.pipe';
import {SegmentHandler} from '../../shared/libraries/segment-handler';
import {AnalyticsHandler} from '../../shared/libraries/analytics-handler';
import {Constants} from '../../shared/constants';
import { Router } from '@angular/router';

@Component({
    selector: 'app-availability-selector',
    templateUrl: './availability-selector.component.html',
    styleUrls: ['./availability-selector.component.scss'],
    providers: [TranslatePipe]
})
export class AvailabilitySelectorComponent implements OnInit {
    /**
     * Header title
     * @var {string}
     */
    public title: string = '';

    /**
     * Header disclaimer
     * @var {string}
     */
    public disclaimer: string = '';

    /**
     * Toggle key to enable standalone calendar
     * @var {string}
     */
    public enableStandaloneCalendarKey: string = 'UI_ENABLE_STANDALONE_CALENDAR';

    /**
     * Selectable segments
     * @var {Array<object>}
     */
    public segments: Array<object> = [];

    /**
     * Current selected segment
     */
    public selectedSegment: string = '';

    /**
     * Application configuration
     * @var {any}
     */
    private _config: any = {};

    /**
     * Environment variables
     * @var {any}
     */
    private _envVars: any = {};

    /**
     * Flag to check if page load analytics event was fired
     * @var {boolean}
     */
    private _pageLoadAnalyticsTriggered: boolean = false;

    /**
     * Event emitter to notify if should render 404 page
     * @var {EventEmitter<boolean>}
     */
    @Output() is404: EventEmitter<boolean> = new EventEmitter<boolean>();

    /**
     * Class constructor
     *
     * @param _apiService
     * @param _configService
     * @param _translatePipe
     * @param _segmentHandler
     * @param _analyticsHandler
     */
    constructor(
        private _apiService: ApiService,
        private _configService: ConfigService,
        private _translatePipe: TranslatePipe,
        private _segmentHandler: SegmentHandler,
        private _analyticsHandler: AnalyticsHandler,
        private _router: Router
    ) {
        this._config = this._configService.getConfig();
        this._envVars = this._configService.getEnvVars();
        this._segmentHandler = this._segmentHandler.initialize(
            this._config.segments, 
            this._config.defaultSegment
        );
    }

    /**
     * On init method
     */
    ngOnInit() {
        this.title = 'header.title';
        this.disclaimer = 'header.disclaimer';

        if (this._envVars[this.enableStandaloneCalendarKey] === true
            && this._segmentHandler.validStandaloneCalendarSegment() === false) {
            this.is404.emit(true);
            return;
        }

        this._getSegmentsInfo();
    }

    /**
     * Get selectable segments information
     *
     * @private
     * @returns {void}
     */
    private _getSegmentsInfo() {
        let segments = this._segmentHandler.getSegments();
        const queryDefaultSegments = this._config.queryParamDefaultSegments;
        const segmentsAreValid = this._segmentHandler.isValidSegments();
        this.selectedSegment = this._segmentHandler.getSelectedSegment();

        //if segments one or more segments are valid, but other invalid
        //redirect guest to the valid segments
        if (segmentsAreValid) {
            this._router.navigate([], {
                queryParams: {
                    segments: segments.join(),
                    defaultSegment: this.selectedSegment
                }
            });
        }
        
        //if the segments set by the guest are invalid redirect
        if (!segmentsAreValid) {
            this._router.navigate([], {
                queryParams: {
                    segments: queryDefaultSegments,
                    defaultSegment: this._config.defaultSegment
                }
            });
            
            //set default segments to show buttons
            segments = queryDefaultSegments.split(',');
        }

        for (const segment of segments) {
            const text = this._translatePipe.transform(`experience.${segment}`);

            this.segments.push({
                'segmentId': segment,
                'text': text
            });
        }
    }

    /**
     * Get the selected segment availability
     *
     * @param event Polymer event
     * @private
     * @returns {void}
     */
    public setSelectedSegment(event) {
        this._apiService.setSelectedSegment(event.detail.value);

        if (this._pageLoadAnalyticsTriggered) {
            this._analyticsHandler
                .setLinkId(Constants.ANALYTICS_LINK_ID_TIER_TOGGLE)
                .setSelectedSegment(event.detail.value)
                .trackAnalytics(Constants.ANALYTICS_EVENT_TYPE_SEGMENT_CHANGED);
        }

        this._apiService.selectedSegment.subscribe(() => {
            if (!this._pageLoadAnalyticsTriggered) {
                this._pageLoadAnalyticsTriggered = true;
            }
        });

        if (event && this._config.redirectedSegments[event.detail.value]) {
           this._redirectSegment(this._config.redirectedSegments[event.detail.value]);
        }
    }

    /**
     * Redirect to another url
     *
     * @private
     * @returns {void}
     */
    public _redirectSegment(segmentUrl) {
        setTimeout(() => {
            window.location.href = segmentUrl;
        }, 500);
    }
}
